/**
 * What a shift cost.
 *
 * The environment already knows the two numbers a fab is actually judged on -
 * tool downtime and wafers that will not ship - and until now it narrated them
 * in prose and threw them away. This reads them back out of the journal.
 *
 * Two deliberate omissions, because both would teach the wrong thing:
 *
 * There is no par. The cheapest possible shift is always "repair the right
 * aspect, take no readings", so scoring against an optimum would score
 * investigation as waste - the exact habit this simulation exists to build. The
 * numbers are reported and left to speak: a wrong call costs four to eight
 * hours, a reading costs one or two, and a student who guesses reads that
 * contrast off their own debrief without being lectured about it.
 *
 * There is no aggregate score. Downtime and out-of-spec work stay separate,
 * because they trade against each other - you can protect the wafer by taking
 * the tool down and protect availability by running it broken - and collapsing
 * them into one figure of merit would hide the only interesting decision on the
 * shift.
 *
 * Not every tool has both. The seven tools with a fault model are judged on
 * downtime AND on what they produced; the thirteen without one are judged on
 * what they produced alone, because they have no repair to order and a downtime
 * of zero would be a fact about the simulation rather than about the student.
 * The debrief shows whichever of the two a tool actually has.
 */

import type { Journal, JournalEvent } from './Journal'

/** One charge against the shift, for the itemised debrief. */
export interface CostLine {
  label: string
  hours: number
}

export interface ShiftCost {
  /** Readings the student chose to earn. The price of being sure. */
  diagnosticHours: number
  /** Preventive maintenance performed during the shift. */
  maintenanceHours: number
  /** The repair the call led to - both of them, if the call was wrong. */
  repairHours: number
  totalHours: number
  /**
   * Outcomes judged against the tool's own spec, and how many failed.
   *
   * Counted as RUNS rather than as units, and that is not a simplification -
   * `count` on a product event does not mean the same thing twice. At the
   * etcher and the scanner it is one wafer. At probe, dicing, bonding, final
   * test and wafer prep it is the number of units that SURVIVED - good die,
   * shipped parts - while `inSpec` is a separate verdict on the tool's
   * metric. Adding `count` into a scrap total would therefore report the good
   * units as the scrapped ones at five tools.
   *
   * One event is one judged outcome everywhere, so that is what is counted.
   * Nothing is lost at the tools whose unit really is one wafer: there, runs
   * out of spec and wafers scrapped are the same number.
   */
  runs: number
  outOfSpec: number
  readingsTaken: number
  questionsAsked: number
  /** Null until the student commits. */
  correct: boolean | null
  lines: CostLine[]
}

/** Events that carry a verdict against the tool's spec. */
const isJudged = (e: JournalEvent) => e.kind === 'wafer' || e.kind === 'product'

/**
 * Cost of one shift, from its events.
 *
 * Takes the slice rather than the journal so it can serve both the live shift
 * and any earlier one in the session tally.
 */
export function shiftCost(events: JournalEvent[]): ShiftCost {
  const lines: CostLine[] = []
  let diagnosticHours = 0
  let maintenanceHours = 0
  let repairHours = 0
  let runs = 0
  let outOfSpec = 0
  let readingsTaken = 0
  let questionsAsked = 0
  let correct: boolean | null = null

  for (const e of events) {
    switch (e.kind) {
      case 'reading':
        readingsTaken++
        if (e.hours) {
          diagnosticHours += e.hours
          lines.push({ label: e.what, hours: e.hours })
        }
        break
      case 'maintenance':
        if (e.hours) {
          maintenanceHours += e.hours
          lines.push({ label: e.label ?? e.action, hours: e.hours })
        }
        break
      case 'question':
        questionsAsked++
        break
      case 'call':
        correct = e.correct
        if (e.hoursSpent) {
          repairHours += e.hoursSpent
          lines.push({ label: e.repairLabel ?? e.label, hours: e.hoursSpent })
        }
        break
      default:
        if (isJudged(e)) {
          runs++
          if (!e.inSpec) outOfSpec++
        }
    }
  }

  return {
    diagnosticHours,
    maintenanceHours,
    repairHours,
    totalHours: diagnosticHours + maintenanceHours + repairHours,
    runs,
    outOfSpec,
    readingsTaken,
    questionsAsked,
    correct,
    lines,
  }
}

/**
 * The shift currently open at one tool.
 *
 * Every tool in the fab writes into the same journal - that is the point of it,
 * since an instructor wants the whole afternoon in order rather than six
 * separate logs. But it means `sinceShiftStart()` returns whatever shift began
 * most recently ANYWHERE, so a student who takes a shift at the etcher, walks
 * to the scanner and takes one there would come back to find the etcher's
 * debrief billing them for the scanner's afternoon.
 *
 * So the slice is taken from this tool's last shift start, and events stamped
 * with a different tool are dropped. Events carrying no tool at all - a wafer
 * run, a recipe change - belong to whichever shift is open, which is the right
 * default: they are recorded by the panel the student is standing at.
 */
export function currentShiftEvents(journal: Journal, tool?: string): JournalEvent[] {
  if (tool === undefined) return journal.sinceShiftStart()

  const events = journal.events
  let start = -1
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]
    if (e.kind === 'shiftStart' && e.tool === tool) {
      start = i
      break
    }
  }
  // No shift formally started here. That is a reachable state rather than an
  // error: the report renders a commit block as soon as the panel mounts, so a
  // student can investigate and call a healthy tool without ever pressing Start
  // shift. Fall back to everything this tool has done, which is what
  // sinceShiftStart() does when the journal holds no shift at all - returning
  // nothing would silently blank the debrief exactly when it is first read.
  if (start < 0) return events.filter((e) => belongsTo(e, tool))
  return events.slice(start).filter((e) => belongsTo(e, tool))
}

/** Is this event this tool's, or unattributed and therefore the open shift's? */
function belongsTo(e: JournalEvent, tool: string): boolean {
  const owner = 'tool' in e ? e.tool : undefined
  return owner === undefined || owner === tool
}

/**
 * Events with no tool of their own are attributed to whichever shift is open.
 * When nothing is open either, they land here rather than being dropped - a
 * student's work going uncounted is worse than a row with a vague name.
 */
export const UNATTRIBUTED = ''

/** What one tool saw this session, across every shift the student ran there. */
export interface StationSummary {
  tool: string
  runs: number
  outOfSpec: number
  hours: number
  shifts: number
  calls: number
  correct: number
  readings: number
  questions: number
}

/**
 * Per-tool totals for the whole session.
 *
 * Deliberately NOT built out of `shiftSlices`. A slice begins at a shift start,
 * and thirteen of the twenty-one tools have no fault model and so never record
 * one - which meant every run at probe, bonding or final test was invisible to
 * the only cross-tool number in the app. Walking the events directly counts a
 * tool's work whether or not it was done under a formal shift.
 */
export function stationSummary(journal: Journal): StationSummary[] {
  const byTool = new Map<string, StationSummary>()
  const of = (tool: string): StationSummary => {
    let row = byTool.get(tool)
    if (!row) {
      row = { tool, runs: 0, outOfSpec: 0, hours: 0, shifts: 0, calls: 0, correct: 0, readings: 0, questions: 0 }
      byTool.set(tool, row)
    }
    return row
  }

  // A wafer run or a question carries no tool, so it belongs to whichever shift
  // is open - the same assumption the live debrief makes.
  let open = UNATTRIBUTED

  for (const e of journal.events) {
    if (e.kind === 'shiftStart') open = e.tool ?? UNATTRIBUTED
    const owner = of(('tool' in e ? e.tool : undefined) ?? open)

    switch (e.kind) {
      case 'shiftStart':
        owner.shifts++
        break
      case 'reading':
        owner.readings++
        owner.hours += e.hours ?? 0
        break
      case 'maintenance':
        owner.hours += e.hours ?? 0
        break
      case 'question':
        owner.questions++
        break
      case 'call':
        owner.calls++
        if (e.correct) owner.correct++
        owner.hours += e.hoursSpent ?? 0
        break
      default:
        if (isJudged(e)) {
          owner.runs++
          if (!e.inSpec) owner.outOfSpec++
        }
    }
  }

  return [...byTool.values()].filter(
    (r) => r.runs > 0 || r.hours > 0 || r.shifts > 0 || r.calls > 0 || r.readings > 0,
  )
}

export interface SessionTally {
  shifts: number
  /** Shifts where a call was committed at all. */
  called: number
  correct: number
  totalHours: number
  totalOutOfSpec: number
  /** Tools the student actually worked at, shift or no shift. */
  tools: number
  totalRuns: number
}

/**
 * The whole session, added up.
 *
 * This is the closest thing to standings the project should have: it compares a
 * student against their own afternoon, which is the comparison that means
 * something, rather than against other students - who are running different
 * faults on different tools, and whose ranking would reward the fastest guess.
 *
 * Built from the per-tool summary rather than from shift slices, so that work
 * done at a tool with no fault model is counted rather than silently dropped.
 */
export function sessionTally(journal: Journal): SessionTally {
  const tally: SessionTally = {
    shifts: 0,
    called: 0,
    correct: 0,
    totalHours: 0,
    totalOutOfSpec: 0,
    tools: 0,
    totalRuns: 0,
  }

  for (const row of stationSummary(journal)) {
    tally.tools++
    tally.shifts += row.shifts
    tally.called += row.calls
    tally.correct += row.correct
    tally.totalHours += row.hours
    tally.totalOutOfSpec += row.outOfSpec
    tally.totalRuns += row.runs
  }

  return tally
}

/**
 * A call the student made before the tool reported, beside what it measured.
 *
 * `about` is the question the technician put to them, `said` is their answer
 * and `measured` is what the tool then said - see src/talk/predict.ts, which
 * writes the event, and TALK.md's "Calling it first".
 */
export interface Call {
  about: string
  said: string
  measured: string
  /**
   * What they said when she then asked WHY it went that way, if she asked and
   * they answered. Not necessarily an explanation - see the `explanation`
   * case in Journal.ts - which is why every label on it says "when asked".
   */
  explained?: string
  at: number
}

/**
 * How many calls a debrief lists. A work order rarely produces more than two
 * or three; the cap is for the student who spends an afternoon at one tool,
 * and it keeps the most recent because those are the ones they can still
 * remember making.
 */
const CALLS_MAX = 8

/**
 * The calls made in this slice, oldest first.
 *
 * The only record anywhere of what the student BELIEVED rather than what they
 * did, and until now it was read in exactly one place - the technician's own
 * brief, at the tool where it happened. Nobody else saw it, including the
 * student who made it. Reading your own wrong call back, in your own words,
 * with the measurement beside it, is the second-strongest teaching moment
 * after making it, and it is the one that survives the conversation.
 *
 * NO VERDICT, and nothing here computes one - the same rule that governs the
 * event itself. The pair is laid out and the student compares it. See the
 * `prediction` case in learn/Journal.ts for why matching prose against a
 * finding would be a grade with nobody accountable for it.
 *
 * Takes the slice rather than the journal, like `shiftCost`, so the order
 * debrief can pass the order's events and a tool's debrief its own shift's.
 */
export function callsMade(events: JournalEvent[]): Call[] {
  const out: Call[] = []
  for (const e of events) {
    if (e.kind === 'prediction') {
      out.push({ about: e.about, said: e.said, measured: e.measured, at: e.at })
      continue
    }
    // Paired by ORDER rather than by matching `about`: the two are recorded
    // one after the other, and two runs in an afternoon can ask the same
    // thing. An explanation with no call before it belongs to nothing and is
    // dropped rather than shown alone.
    if (e.kind === 'explanation' && out.length) out[out.length - 1].explained = e.said
  }
  return out.slice(-CALLS_MAX)
}

/**
 * Split the session into shifts.
 *
 * Anything before the first `shiftStart` is free play - the student walking in
 * and moving sliders before taking a shift - and is not a shift, so it is
 * dropped rather than counted as one with no call.
 */
export function shiftSlices(events: JournalEvent[]): JournalEvent[][] {
  const out: JournalEvent[][] = []
  const owners: (string | undefined)[] = []
  for (const e of events) {
    if (e.kind === 'shiftStart') {
      out.push([e])
      owners.push(e.tool)
      continue
    }
    if (out.length === 0) continue
    // Product finished at another tool is that tool's business, even when it
    // happened while this shift was open - a student can walk away mid-shift.
    const owner = owners[owners.length - 1]
    if (owner !== undefined && !belongsTo(e, owner)) continue
    out[out.length - 1].push(e)
  }
  return out
}
